import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import { useJsApiLoader } from '@react-google-maps/api';

import { logData } from '../../utils/debug.util';
import { theme } from '../../global-styles/colors';
import { GOOGLE_API_KEY } from '../../constants/env';
import { GOOGLE_MAPS_LIBRARIES } from '../../components/google-maps/GoogleMapsLibraries';

const libraries = GOOGLE_MAPS_LIBRARIES;
const DEFAULT_ROUTE_COLOR = theme.primary.primary50;

const getDirectionsRenderer = (strokeColor: string) =>
  new window.google.maps.DirectionsRenderer({
    draggable: false,
    hideRouteList: true,
    markerOptions: {
      opacity: 0,
      anchorPoint: new window.google.maps.Point(0, -10),
      zIndex: 99999,
    },
    polylineOptions: {
      strokeOpacity: 1,
      strokeColor: strokeColor,
    },
  });

const getDirectionsService = () => new window.google.maps.DirectionsService();

const useGoogleRoutes = (strokeColor: string = DEFAULT_ROUTE_COLOR) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    i18n: { language },
  } = useTranslation(['components']);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_API_KEY || '',
    // language,
    language: 'es',
    libraries: libraries,
  });

  const directionRendererRef = useRef(isLoaded ? getDirectionsRenderer(strokeColor) : undefined);

  const directionsServiceRef = useRef(isLoaded ? getDirectionsService() : undefined);

  useEffect(() => {
    if (isLoaded) {
      directionRendererRef.current = getDirectionsRenderer(strokeColor);
      directionsServiceRef.current = getDirectionsService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  const renderRoute = useCallback(
    (directions: google.maps.DirectionsResult | null, map: google.maps.Map | null) => {
      if (!directionRendererRef.current) {
        directionRendererRef.current = getDirectionsRenderer(strokeColor);
      }
      directionRendererRef.current.setOptions({
        polylineOptions: { strokeOpacity: 1, strokeColor },
      });
      directionRendererRef.current.setMap(map);
      directionRendererRef.current.set('directions', null);
      directionRendererRef.current.setDirections(directions);
    },
    [strokeColor],
  );

  const getRoute = useCallback(
    (
      origin: google.maps.DirectionsRequest['origin'],
      destination: google.maps.DirectionsRequest['destination'],
      setArrival?: (time: moment.Moment) => void,
      onFinish?: () => void,
    ): Promise<google.maps.DirectionsResult | null> => {
      if (!directionsServiceRef.current) {
        directionsServiceRef.current = getDirectionsService();
      }

      return new Promise((resolve) => {
        void directionsServiceRef.current?.route(
          {
            travelMode: google.maps.TravelMode.DRIVING,
            origin,
            destination,
          },
          (response, status) => {
            logData('Google Maps route response', { response, status });

            if (status === google.maps.DirectionsStatus.OK && response) {
              const duration = response.routes?.[0]?.legs?.[0]?.duration;
              if (duration && setArrival) {
                setArrival(moment().add(duration.value, 'seconds'));
              }
              onFinish?.();
              resolve(response);
              return;
            }

            onFinish?.();
            resolve(null);
          },
        );
      });
    },
    [],
  );

  return { renderRoute, getRoute };
};

export default useGoogleRoutes;
